<script>
import { onMounted, ref, inject } from "vue";
import { fetchById as fetchPrescriptionById } from "@/use/repositories/prescriptions/fetchById";
import { dateFormatter } from "@/use/utilities/time/dateFormatter";
import SliderImage from "@/components/general/SliderImages";
import { fetchAll as fetchMessages } from "@/use/repositories/chat/message/fetchAll";
import MessageRender from "@/components/chat/MessageRender.vue";
import { useMessageTransform } from "@/composables/useMessageTransform";
import { useStateIcon } from "@/composables/useStateIcon";
import Spinner from "@/components/general/Spinner.vue";
import { api as viewerApi } from "v-viewer";

export default {
  name: "GlobalPrescriptionNotes",
  components: { SliderImage, MessageRender, Spinner },
  props: {
    prescriptionId: { type: Number, required: true },
    chatChannelId: { type: Number, default: null },
  },
  emits: ["close"],
  setup(props) {
    const prescriptionTests = ref([]);
    const isLoading = ref(false);
    const cardTitles = ['Prova di Lavoro', 'Feedback Clinica', 'Note Lab', 'Chat'];
    const { groupMessagesByDate } = useMessageTransform();
    const { states } = inject("constants");
    const { getPrescriptionTestStateIcon } = useStateIcon();
    const getMessages = async (fromDate, toDate) => {
      const { data: { data } } = await fetchMessages({
        action: "by_channel_and_date",
        channel_id: props.chatChannelId,
        from_date: `${dateFormatter(fromDate, { iso: true, dash: true })}:00`,
        to_date: `${dateFormatter(toDate, { iso: true, dash: true })}:00`,
      });

      return groupMessagesByDate(data);
    };

    const calculateDateRange = (prescriptionTest) => {
      return {
        fromDate: prescriptionTest.created_at,
        toDate: new Date().toISOString(),
      };
    };

    const previewImage = (message, index) => {
      viewerApi({
        images: message.attachments.map((url) => {
          return {
            "data-source": url,
          }
        }),
        options: {
          toolbar: true,
          url: "data-source",
          initialViewIndex: index,
        },
      });
    };

    onMounted(async () => {
      isLoading.value = true;
      try {
        const { prescription } = await fetchPrescriptionById(
          props.prescriptionId,
          'with_prescription_tests:with_trashed',
          false
        );

        const currentTest = prescription.prescription_tests.find(
          test => test.id === prescription.current_test_id
        );

        if (currentTest) {
          const { fromDate, toDate } = calculateDateRange(currentTest);
          const messages = await getMessages(fromDate, toDate);
          currentTest.messages = messages;
          prescriptionTests.value = [currentTest];
        } else {
          prescriptionTests.value = [];
        }
      } finally {
        isLoading.value = false;
      }
    });

    return {
      prescriptionTests,
      dateFormatter,
      SliderImage,
      MessageRender,
      getMessages,
      states,
      getPrescriptionTestStateIcon,
      isLoading,
      cardTitles,
      previewImage
    };
  }
};
</script>
<template>
  <div class="p-4">
    <div v-if="isLoading" class="mb-4">
      <div class="row row-cols-1 row-cols-md-2 row-cols-lg-4 g-3">
        <div v-for="title in cardTitles" :key="title" class="col">
          <div class="card h-100">
            <div class="card-header">
              <span class="fw-semibold">{{ title }}</span>
            </div>
            <div class="card-body d-flex justify-content-center align-items-center">
              <Spinner />
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-else v-for="prescriptionTest in prescriptionTests" :key="prescriptionTest.id" class="mb-4">
      <div v-if="!prescriptionTest.deleted_at" class="row row-cols-1 row-cols-md-2 row-cols-lg-4 g-3">
        <div class="col">
          <div class="card h-100">
            <div class="card-header">
              <span class="fw-semibold">Prova di Lavoro</span>
            </div>
            <div class="card-body">
              <div class="fw-semibold mb-2">
                <img
                  v-if="prescriptionTest.test_work_queue?.state?.slug"
                  :src="require(`@/assets/icons/${getPrescriptionTestStateIcon(prescriptionTest.test_work_queue.state.slug)}.svg`)"
                  :alt="`status-${prescriptionTest.test_work_queue.state.slug}`"
                  width="25"
                  class="mx-3" />
                <span>{{ prescriptionTest.name }}</span>
              </div>
              <div class="small text-muted">
                <template v-if="[states.SHIPPED, states.NEGATIVE_FEEDBACK].includes(prescriptionTest.test_work_queue?.state?.slug)">
                  <div class="test-info d-flex flex-wrap mt-1">
                    <small class="d-flex align-items-center me-2 text-nowrap fw-bold">
                      <span class="material-icons-outlined me-2">local_shipping</span>
                      <span>{{ prescriptionTest.test_work_queue?.shipping_codes?.length ? dateFormatter(prescriptionTest.test_work_queue?.shipping_codes[0]?.created_at) : "" }}</span>
                    </small>
                  </div>
                </template>
                <template v-if="prescriptionTest.test_work_queue?.delivery_date">
                  <small class="d-flex align-items-center me-2 text-nowrap fw-bold">
                    <span class="material-icons-outlined me-2"> flag </span>
                    <span>{{ dateFormatter(prescriptionTest.test_work_queue?.delivery_date) }}</span>
                  </small>
                </template>
              </div>
            </div>
          </div>
        </div>
        <div class="col">
          <div class="card h-100">
            <div class="card-header">
              <span class="fw-semibold">Feedback Clinica</span>
            </div>
            <div class="card-body">
              <div v-if="typeof prescriptionTest.feedback === 'number'" class="d-flex m-2">
                <img
                  class="mb-2 ms-2"
                  :src="require(`@/assets/icons/${prescriptionTest.feedback ? 'like-green' : 'dislike-red'}.svg`)"
                  width="25"
                  alt="note-icon" />
              </div>
              <div>
                <div class="small text-muted mb-2">
                  <span>{{ prescriptionTest.feedback_note ? `Feedback: ${prescriptionTest.feedback_note}` : "Nessun feedback presente" }}</span>
                </div>
                <div class="small text-muted mb-2">
                  {{ prescriptionTest.notes ? `Note: ${prescriptionTest.notes}` : "Nessuna nota presente" }}
                </div>
              </div>
            </div>
          </div>
        </div>
        <!-- Note Lab -->
        <div class="col">
          <div class="card h-100">
            <div class="card-header">
              <span class="fw-semibold">Note Lab</span>
            </div>
            <div class="card-body">
              <div class="small text-muted">
                <template v-if="prescriptionTest?.laboratory_notes?.length">
                  <ul class="list-unstyled mb-0">
                    <li v-for="note in prescriptionTest.laboratory_notes" :key="note.id">
                      <div class="d-flex flex-column w-100">
                        <small class="text-start fw-bold">{{ note.user?.first_name }} {{ note.user?.last_name }}</small>
                        <p class="card-text text-start">
                          {{ note.note }}
                        </p>
                        <small class="d-flex justify-content-end text-muted datetime">
                          {{ dateFormatter(note.created_at) }}
                        </small>
                        <template v-if="note.media?.length">
                          <SliderImage
                            :elements="note.media.map((item) => {
                          return {
                            createdAt: item.createdAt,
                            temporaryUrl: item.temporary_url,
                          }
                        })"
                            :item-to-show="note.media?.length"
                          />
                        </template>
                      </div>
                    </li>
                  </ul>
                </template>
                <span v-else>Nessuna nota presente</span>
              </div>
            </div>
          </div>
        </div>
        <div class="col">
          <div class="card h-100">
            <div class="card-header">
              <span class="fw-semibold">Chat</span>
            </div>
            <div class="card-body p-0">
              <MessageRender
                v-if="prescriptionTest.messages?.length"
                :messages="prescriptionTest.messages"
                @preview-image="previewImage" />
              <div v-else class="small text-muted p-3">
                <span>Nessun messaggio presente</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <hr class="mt-4 mb-0" />
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import "@/scss/abstracts/_variables.scss";
.card-body {
  max-height: 70vh;
  overflow-y: auto;
}
.card-header {
  background-color: $home_violet !important;
  border-left: 0.5rem solid $orange;
}
</style>
